import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';

// Social media icons as inline SVG for reliability
const FacebookIcon = () => (
  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

const TiktokIcon = () => (
  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
  </svg>
);

export default function SocialMediaSection({ childId, child, onUpdate, readOnly = false }) {
  const [saving, setSaving] = useState(false);
  const [handles, setHandles] = useState({
    facebook_handle: child?.facebook_handle || '',
    instagram_handle: child?.instagram_handle || '',
    tiktok_handle: child?.tiktok_handle || ''
  });

  const getSocialUrl = (platform, handle) => {
    if (!handle) return null;
    const cleanHandle = handle.replace('@', '').trim();
    switch (platform) {
      case 'facebook': return `https://facebook.com/${cleanHandle}`;
      case 'instagram': return `https://instagram.com/${cleanHandle}`;
      case 'tiktok': return `https://tiktok.com/@${cleanHandle}`;
      default: return null;
    }
  };

  const saveHandles = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/children/${childId}`, handles);
      toast.success('Social media handles updated!');
      if (onUpdate) onUpdate(handles);
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Read-only view for cards
  if (readOnly) {
    const hasSocials = child?.facebook_handle || child?.instagram_handle || child?.tiktok_handle;
    if (!hasSocials) return null;
    
    return (
      <div className="flex gap-2" data-testid="social-icons">
        {child?.facebook_handle && (
          <a href={getSocialUrl('facebook', child.facebook_handle)} target="_blank" rel="noopener noreferrer"
             className="p-2 bg-blue-600/20 hover:bg-blue-600/40 rounded-full text-blue-400 transition-colors"
             title={`Facebook: ${child.facebook_handle}`}>
            <FacebookIcon />
          </a>
        )}
        {child?.instagram_handle && (
          <a href={getSocialUrl('instagram', child.instagram_handle)} target="_blank" rel="noopener noreferrer"
             className="p-2 bg-pink-500/20 hover:bg-pink-500/40 rounded-full text-pink-400 transition-colors"
             title={`Instagram: ${child.instagram_handle}`}>
            <InstagramIcon />
          </a>
        )}
        {child?.tiktok_handle && (
          <a href={getSocialUrl('tiktok', child.tiktok_handle)} target="_blank" rel="noopener noreferrer"
             className="p-2 bg-zinc-600/20 hover:bg-zinc-600/40 rounded-full text-white transition-colors"
             title={`TikTok: ${child.tiktok_handle}`}>
            <TiktokIcon />
          </a>
        )}
      </div>
    );
  }

  // Editable view
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4" data-testid="social-media-edit">
      <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-400 mb-4">Social Media</h3>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-600/20 rounded text-blue-400"><FacebookIcon /></div>
          <Input
            value={handles.facebook_handle}
            onChange={(e) => setHandles(prev => ({ ...prev, facebook_handle: e.target.value }))}
            placeholder="Facebook username"
            className="flex-1 bg-zinc-950 border-zinc-800"
          />
        </div>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-pink-500/20 rounded text-pink-400"><InstagramIcon /></div>
          <Input
            value={handles.instagram_handle}
            onChange={(e) => setHandles(prev => ({ ...prev, instagram_handle: e.target.value }))}
            placeholder="Instagram username"
            className="flex-1 bg-zinc-950 border-zinc-800"
          />
        </div>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-zinc-600/20 rounded text-white"><TiktokIcon /></div>
          <Input
            value={handles.tiktok_handle}
            onChange={(e) => setHandles(prev => ({ ...prev, tiktok_handle: e.target.value }))}
            placeholder="TikTok username"
            className="flex-1 bg-zinc-950 border-zinc-800"
          />
        </div>
      </div>
      <Button onClick={saveHandles} disabled={saving} className="w-full mt-4 bg-[#02b5ff] hover:bg-[#0099dd]">
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
        Save Social Media
      </Button>
    </div>
  );
}
